import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusSelect } from "@/components/StatusSelect";
import { PrioritySelect } from "@/components/PrioritySelect";
import { AssigneeSelect } from "@/components/AssigneeSelect";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import TaskLabels from "./TaskLabels";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { DueDateSelect } from "@/components/DueDateSelect";
import { FormSubmissionLink } from "@/components/ItemLink";
import { TbFileText, TbExternalLink } from 'react-icons/tb';

export default function TaskSidebar({ task, onTaskChange, organizationSlug, isLoading }) {
    if (isLoading) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-normal text-neutral-500 dark:text-neutral-600">Bot Insights</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-7">
                        {/* Status, Priority, Assignee skeletons */}
                        <div className="flex flex-col space-y-4">
                            <div className="h-7 w-24 bg-muted animate-pulse rounded-md" />
                            <div className="h-7 w-32 bg-muted animate-pulse rounded-md" />
                            <div className="h-7 w-28 bg-muted animate-pulse rounded-md" />
                        </div>
                
                        {/* Due Date skeleton */}
                        <div className="h-15">
                            <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600 mb-2">Due Date</div>
                            <div className="h-7 w-24 bg-muted animate-pulse rounded-md" />
                        </div>

                        {/* Labels skeleton */}
                        <div className="h-15">
                            <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600 mb-2">Labels</div>
                            <div className="h-7 w-32 bg-muted animate-pulse rounded-md" />
                        </div>

                        {/* Active Weeks skeleton */}
                        <div className="h-13">
                            <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600 mb-2">Active Weeks</div>
                            <div className="flex gap-2">
                                <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
                                <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-base font-normal text-neutral-500 dark:text-neutral-600">Bot Insights</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-7">
                    <div className="flex flex-col space-y-4">
                        <StatusSelect
                            showLabel
                            task={task}
                            onTaskChange={onTaskChange}
                        />
                        <PrioritySelect
                            showLabel
                            task={task}
                            onTaskChange={onTaskChange}
                        />
                        <AssigneeSelect
                            showName
                            task={task}
                            organizationSlug={organizationSlug}
                            onTaskChange={onTaskChange}
                        />
                    </div>

                    {/* Due Date */}
                    <DueDateSelect
                        task={task}
                        showLabel
                        organizationSlug={organizationSlug}
                        onTaskChange={onTaskChange}
                    />

                    {/* Labels */}
                    <TaskLabels
                        task={task}
                        onTaskChange={onTaskChange}
                        organizationSlug={organizationSlug}
                    />

                    {/* Active Weeks */}
                    <div className="space-y-2">
                        <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600">Active Weeks</div>
                        <div className="-ml-2">
                            {task.weeks && task.weeks.length > 0 ? (
                                <div className="text-sm text-muted-foreground">
                                    <TooltipProvider delayDuration={0}>
                                        {task.weeks.map((week, index) => (
                                            <span key={index}>
                                                <Tooltip>
                                                    <TooltipTrigger className="cursor-pointer text-muted-foreground px-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-primary">
                                                        Week {week.weekNumber}{index < task.weeks.length - 1 && <span>,</span>}
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {new Date(week.startDate).toLocaleDateString('en-US', { 
                                                            month: 'short', 
                                                            day: 'numeric',
                                                            year: 'numeric'
                                                        })} 
                                                        {' – '}
                                                        {new Date(week.endDate).toLocaleDateString('en-US', { 
                                                            month: 'short', 
                                                            day: 'numeric',
                                                            year: 'numeric'
                                                        })}
                                                    </TooltipContent>
                                                </Tooltip>
                                                
                                            </span>
                                        ))}
                                    </TooltipProvider>
                                </div>
                            ) : (
                                <div className="text-sm text-muted-foreground">No active weeks</div>
                            )}
                        </div>
                    </div>

                    {/* Submission Origin */}
                    {task.createdFromSubmission && (
                        <div className="space-y-2">
                            <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600">Originated From</div>
                            <FormSubmissionLink
                                submission={task.createdFromSubmission}
                                organizationSlug={organizationSlug}
                            />
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}